import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function CaptivePortal() {
  const { t } = useTranslation();
  const [step, setStep] = useState<'packages' | 'phone' | 'otp' | 'payment' | 'credentials'>('packages');
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });

  const packages = [
    { id: 1, name: 'الباقة الأساسية', duration: 'ساعة واحدة', price: 500, speed: '5 Mbps' },
    { id: 2, name: 'الباقة القياسية', duration: '6 ساعات', price: 2000, speed: '10 Mbps' },
    { id: 3, name: 'الباقة المميزة', duration: 'يوم كامل', price: 3500, speed: '20 Mbps' },
    { id: 4, name: 'الباقة الشاملة', duration: 'أسبوع كامل', price: 15000, speed: '50 Mbps' }
  ];

  const paymentMethods = [
    { id: 'orange', name: 'Orange Money', icon: 'ri-smartphone-line', color: 'from-orange-500 to-orange-600' },
    { id: 'moov', name: 'Moov Money', icon: 'ri-phone-line', color: 'from-blue-500 to-blue-600' },
    { id: 'mtn', name: 'MTN Mobile Money', icon: 'ri-mobile-download-line', color: 'from-yellow-500 to-yellow-600' },
    { id: 'wave', name: 'Wave', icon: 'ri-wallet-3-line', color: 'from-purple-500 to-purple-600' }
  ];

  const handlePackageSelect = (pkg: any) => {
    setSelectedPackage(pkg);
    setStep('phone');
  };

  const handlePhoneSubmit = () => {
    if (phoneNumber.length >= 8) {
      // محاكاة إرسال OTP
      setStep('otp');
    }
  };

  const handleOtpSubmit = () => {
    if (otp.length === 6) {
      setStep('payment');
    }
  };

  const handlePaymentSubmit = () => {
    if (paymentMethod) {
      // محاكاة عملية الدفع
      const username = `user${Math.floor(Math.random() * 10000)}`;
      const password = Math.random().toString(36).slice(-8);
      setCredentials({ username, password });
      setStep('credentials');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-6 px-6 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-wifi-line text-3xl"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold">مرحباً بك في شبكة الواي فاي</h1>
              <p className="text-white/90">اختر باقتك وابدأ التصفح الآن</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
            <i className="ri-signal-wifi-3-line text-xl"></i>
            <span className="font-semibold">متصل</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {['الباقات', 'رقم الهاتف', 'التحقق', 'الدفع', 'بيانات الدخول'].map((label, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex flex-col items-center ${index <= ['packages', 'phone', 'otp', 'payment', 'credentials'].indexOf(step) ? 'opacity-100' : 'opacity-40'}`}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                    index <= ['packages', 'phone', 'otp', 'payment', 'credentials'].indexOf(step)
                      ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="text-sm mt-2 font-medium text-gray-700">{label}</span>
                </div>
                {index < 4 && (
                  <div className={`w-16 h-1 mx-2 ${
                    index < ['packages', 'phone', 'otp', 'payment', 'credentials'].indexOf(step)
                      ? 'bg-gradient-to-r from-blue-600 to-green-600'
                      : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Package Selection */}
        {step === 'packages' && (
          <div className="animate-fade-in">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">اختر الباقة المناسبة لك</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => handlePackageSelect(pkg)}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border-2 border-transparent hover:border-blue-500"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                      <p className="text-gray-600 flex items-center gap-2">
                        <i className="ri-time-line"></i>
                        {pkg.duration}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">{pkg.price}</div>
                      <div className="text-sm text-gray-500">فرنك</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-green-600 mb-4">
                    <i className="ri-speed-line text-xl"></i>
                    <span className="font-semibold">السرعة: {pkg.speed}</span>
                  </div>
                  <button className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all whitespace-nowrap">
                    اختر هذه الباقة
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Phone Number */}
        {step === 'phone' && selectedPackage && (
          <div className="animate-fade-in">
            <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-4xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">أدخل رقم هاتفك</h2>
                <p className="text-gray-600">سنرسل لك رمز التحقق عبر رسالة نصية</p>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">الباقة المختارة:</span>
                  <span className="text-blue-600 font-bold">{selectedPackage.name}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-gray-700 font-medium">المبلغ:</span>
                  <span className="text-green-600 font-bold">{selectedPackage.price} فرنك</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">رقم الهاتف</label>
                <div className="relative">
                  <i className="ri-phone-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="أدخل رقم هاتفك"
                    className="w-full pr-12 pl-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-lg"
                    dir="ltr"
                  />
                </div>
              </div>

              <button
                onClick={handlePhoneSubmit}
                disabled={phoneNumber.length < 8}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                إرسال رمز التحقق
              </button>
            </div>
          </div>
        )}

        {/* Step 3: OTP Verification */}
        {step === 'otp' && (
          <div className="animate-fade-in">
            <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lock-password-line text-4xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">أدخل رمز التحقق</h2>
                <p className="text-gray-600">تم إرسال رمز مكون من 6 أرقام إلى</p>
                <p className="text-blue-600 font-bold mt-1" dir="ltr">{phoneNumber}</p>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">رمز التحقق (OTP)</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-center text-2xl font-bold tracking-widest"
                  dir="ltr"
                  maxLength={6}
                />
              </div>

              <button
                onClick={handleOtpSubmit}
                disabled={otp.length !== 6}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap mb-4"
              >
                تحقق من الرمز
              </button>

              <button className="w-full text-blue-600 font-semibold hover:underline whitespace-nowrap cursor-pointer">
                إعادة إرسال الرمز
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Payment Method */}
        {step === 'payment' && (
          <div className="animate-fade-in">
            <div className="bg-white rounded-2xl p-8 shadow-xl max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">اختر طريقة الدفع</h2>
                <p className="text-gray-600">ادفع بأمان عبر الطريقة المفضلة لديك</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      paymentMethod === method.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${method.color} rounded-xl flex items-center justify-center`}>
                        <i className={`${method.icon} text-2xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg">{method.name}</h3>
                        <p className="text-sm text-gray-600">دفع آمن ومشفر</p>
                      </div>
                      {paymentMethod === method.id && (
                        <i className="ri-checkbox-circle-fill text-2xl text-blue-600 mr-auto"></i>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700 font-medium">الباقة:</span>
                  <span className="text-gray-900 font-bold">{selectedPackage.name}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700 font-medium">المدة:</span>
                  <span className="text-gray-900 font-bold">{selectedPackage.duration}</span>
                </div>
                <div className="border-t border-gray-300 pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 font-bold text-lg">المبلغ الإجمالي:</span>
                    <span className="text-green-600 font-bold text-2xl">{selectedPackage.price} فرنك</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePaymentSubmit}
                disabled={!paymentMethod}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                إتمام الدفع
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Credentials */}
        {step === 'credentials' && (
          <div className="animate-fade-in">
            <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-4xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-green-600 mb-2">تم الدفع بنجاح!</h2>
                <p className="text-gray-600">استخدم بيانات الدخول التالية للاتصال بالإنترنت</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 mb-6">
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">اسم المستخدم</label>
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <p className="text-xl font-bold text-gray-900 text-center" dir="ltr">{credentials.username}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">كلمة المرور</label>
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <p className="text-xl font-bold text-gray-900 text-center" dir="ltr">{credentials.password}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-2xl text-yellow-600 flex-shrink-0"></i>
                  <div>
                    <p className="text-gray-700 font-medium mb-2">معلومات مهمة:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• تم إرسال بيانات الدخول إلى رقم هاتفك</li>
                      <li>• احتفظ ببيانات الدخول في مكان آمن</li>
                      <li>• صلاحية الباقة: {selectedPackage.duration}</li>
                      <li>• السرعة: {selectedPackage.speed}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 rounded-xl font-bold hover:shadow-lg transition-all whitespace-nowrap"
              >
                الاتصال بالإنترنت الآن
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
